const GALLERY_SELECTOR = '.gallery-items'
const ITEM_SELECTOR = ':scope > .gallery-item'
const EDITING_CLASS = 'glbbtns-is-editing'
const MOBILE_MEDIA = window.matchMedia('(max-width: 29.99em)')

let layoutScheduled = false


function waitForImage(image) {
  if (image.complete) {
    if (typeof image.decode === 'function') {
      return image.decode().catch(() => { })
    }

    return Promise.resolve()
  }

  return new Promise(resolve => {
    image.addEventListener('load', resolve, { once: true })
    image.addEventListener('error', resolve, { once: true })
  })
}

async function waitForGalleryImages(gallery) {
  const images = Array.from(gallery.querySelectorAll('img'))

  if (!images.length) {
    return
  }

  /*
   * Non lasciamo la gallery nascosta indefinitamente se una risorsa
   * è particolarmente lenta o non risponde.
   */
  await Promise.race([
    Promise.allSettled(images.map(waitForImage)),
    new Promise(resolve => {
      window.setTimeout(resolve, 3000)
    }),
  ])
}

function markGalleryReady(gallery) {
  gallery.classList.add('gallery-items--ready')
  gallery.classList.remove('gallery-items--pending')
}

function scheduleGalleryLayout() {
  if (layoutScheduled) {
    return
  }

  layoutScheduled = true

  window.requestAnimationFrame(() => {
    layoutScheduled = false

    document
      .querySelectorAll(GALLERY_SELECTOR)
      .forEach(layoutGallery)
  })
}

function layoutGallery(gallery) {
  const items = Array.from(
    gallery.querySelectorAll(ITEM_SELECTOR)
  )

  const isEditing =
    document.documentElement.classList.contains(EDITING_CLASS)

  const mustUseSingleColumn =
    MOBILE_MEDIA.matches || items.length < 2

  if (isEditing || mustUseSingleColumn) {
    resetGalleryLayout(gallery, items)
    markGalleryReady(gallery)
    return
  }

  gallery.classList.add('gallery-items--masonry')

  /*
   * Prima rimuoviamo gli span precedenti, altrimenti una misura
   * eseguita dopo un resize potrebbe usare l'altezza già assegnata.
   */
  for (const item of items) {
    item.style.removeProperty('grid-row-end')
  }

  /*
   * Forza il browser ad applicare il reset prima di misurare.
   */
  void gallery.offsetHeight

  const galleryStyle = window.getComputedStyle(gallery)
  const rowHeight = Number.parseFloat(galleryStyle.gridAutoRows)
  const rowGap = Number.parseFloat(galleryStyle.rowGap)

  if (
    !Number.isFinite(rowHeight) ||
    rowHeight <= 0 ||
    !Number.isFinite(rowGap)
  ) {
    resetGalleryLayout(gallery, items)
    return
  }

  for (const item of items) {
    const itemHeight = item.scrollHeight

    const rowSpan = Math.max(
      1,
      Math.ceil(
        (itemHeight + rowGap) /
        (rowHeight + rowGap)
      )
    )

    item.style.gridRowEnd = `span ${rowSpan}`
  }

  markGalleryReady(gallery)
}

function resetGalleryLayout(gallery, items) {
  gallery.classList.remove('gallery-items--masonry')

  for (const item of items) {
    item.style.removeProperty('grid-row-end')
  }
}

function observeGalleryImages(gallery) {
  const images = gallery.querySelectorAll('img')

  for (const image of images) {
    if (image.complete) {
      continue
    }

    image.addEventListener('load', scheduleGalleryLayout, {
      once: true,
    })

    image.addEventListener('error', scheduleGalleryLayout, {
      once: true,
    })
  }
}

function initialiseGalleryMasonry() {
  const galleries = Array.from(
    document.querySelectorAll(GALLERY_SELECTOR)
  )

  if (!galleries.length) {
    return
  }

  for (const gallery of galleries) {
    observeGalleryImages(gallery)
  }

  /*
   * Ricalcola soltanto quando cambia la larghezza della gallery.
   * In questo modo evitiamo cicli causati dalla variazione della
   * sua altezza durante il masonry.
   */
  const galleryWidths = new WeakMap()

  const resizeObserver = new ResizeObserver(entries => {
    let widthChanged = false

    for (const entry of entries) {
      const previousWidth = galleryWidths.get(entry.target)
      const currentWidth = entry.contentRect.width

      if (previousWidth !== currentWidth) {
        galleryWidths.set(entry.target, currentWidth)
        widthChanged = true
      }
    }

    if (widthChanged) {
      scheduleGalleryLayout()
    }
  })

  for (const gallery of galleries) {
    galleryWidths.set(
      gallery,
      gallery.getBoundingClientRect().width
    )

    resizeObserver.observe(gallery)
  }

  /*
   * Quando si entra in modifica vengono rimossi gli span.
   * Quando si esce, il masonry viene ricalcolato.
   */
  const editModeObserver = new MutationObserver(
    scheduleGalleryLayout
  )

  editModeObserver.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ['class'],
  })

  /*
   * Gestisce anche eventuali fotografie aggiunte dinamicamente.
   */
  const contentObserver = new MutationObserver(mutations => {
    let galleryChanged = false

    for (const mutation of mutations) {
      if (mutation.type !== 'childList') {
        continue
      }

      galleryChanged = true
      observeGalleryImages(mutation.target)
    }

    if (galleryChanged) {
      scheduleGalleryLayout()
    }
  })

  for (const gallery of galleries) {
    contentObserver.observe(gallery, {
      childList: true,
      subtree: true,
    })
  }

  MOBILE_MEDIA.addEventListener(
    'change',
    scheduleGalleryLayout
  )

  async function runInitialLayout() {
    await Promise.all(
      galleries.map(waitForGalleryImages)
    )

    /*
     * Un frame permette al browser di applicare le dimensioni
     * definitive delle immagini prima delle misurazioni.
     */
    window.requestAnimationFrame(() => {
      for (const gallery of galleries) {
        layoutGallery(gallery)
      }
    })
  }

  window.addEventListener('load', scheduleGalleryLayout)

  if (document.fonts?.ready) {
    document.fonts.ready.then(scheduleGalleryLayout)
  }

  runInitialLayout()
}

if (document.readyState === 'loading') {
  document.addEventListener(
    'DOMContentLoaded',
    initialiseGalleryMasonry,
    { once: true }
  )
} else {
  initialiseGalleryMasonry()
}
